import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../auth/AuthProvider";
import { deleteAccount } from "../lib/api";
import { friendly } from "../lib/errors";
import {
  loadTheme, saveTheme, SWATCH, THEME_LABEL, THEME_NAMES, type ThemeName,
} from "../lib/theme";
import Sheet from "../components/Sheet";

/** Account Settings — ported from AccountSettingsView.swift (the gear icon). */
export default function AccountSettings() {
  const { uid, user, signOutUser } = useAuth();
  const navigate = useNavigate();

  const [age, setAge] = useState<number | null>(null);
  const [memberSince, setMemberSince] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const [{ theme, intensity }, setAppearance] = useState(loadTheme);
  // Persist locally AND to the account, so the theme follows the login across
  // Safari, the installed app, and other devices (iOS separates their storage).
  function persistRemote(t: ThemeName, i: number) {
    if (!uid) return;
    void updateDoc(doc(db, "users", uid), { themeName: t, themeIntensity: i }).catch(() => {});
  }
  function setTheme(t: ThemeName) {
    saveTheme(t, intensity);
    setAppearance({ theme: t, intensity });
    persistRemote(t, intensity);
  }
  function setIntensity(i: number) {
    saveTheme(theme, i);
    setAppearance({ theme, intensity: i });
    persistRemote(theme, i);
  }

  const email = user?.email ?? "—";
  const provider = user?.providerData[0]?.providerId;
  const signInMethod = provider === "apple.com" ? "Apple"
    : provider === "google.com" ? "Google"
    : provider === "password" ? "Email" : provider ?? "—";

  useEffect(() => {
    if (!uid) return;
    void getDoc(doc(db, "users", uid)).then((s) => {
      const d = s.data();
      setAge((d?.age as number) ?? null);
      const ts = d?.createdAt as { toDate?: () => Date } | undefined;
      if (ts?.toDate) {
        setMemberSince(ts.toDate().toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }));
      }
    });
  }, [uid]);

  async function doDelete() {
    if (deleteText !== "DELETE" || deleting) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteAccount();
      await signOutUser();
    } catch (e) {
      setError(friendly(e));
      setDeleting(false);
    }
  }

  return (
    <div className="screen">
      <div className="profile-header">
        <Link to="/profile" className="icon-btn" aria-label="Back" style={{ textDecoration: "none" }}>‹</Link>
        <h1 style={{ margin: 0, fontSize: 22 }}>Account Settings</h1>
        <span style={{ width: 40 }} />
      </div>

      <h2>Account</h2>
      <div className="settings-card">
        <div className="settings-row"><span>Email</span><span className="muted">{email}</span></div>
        <div className="settings-row"><span>Sign-in method</span><span className="muted">{signInMethod}</span></div>
        {age !== null && <div className="settings-row"><span>Age</span><span className="muted">{age}</span></div>}
        {memberSince && <div className="settings-row"><span>Member since</span><span className="muted">{memberSince}</span></div>}
      </div>
      <p className="muted" style={{ fontSize: 12 }}>
        Your age is set from the birthday you entered at signup and can't be changed.
      </p>

      <h2>Appearance</h2>
      <div className="swatch-row">
        {THEME_NAMES.map((t) => (
          <button
            key={t}
            className={"swatch" + (theme === t ? " swatch-on" : "")}
            style={{ background: SWATCH[t] }}
            aria-label={THEME_LABEL[t]}
            title={THEME_LABEL[t]}
            onClick={() => setTheme(t)}
          >
            {theme === t && <span className="swatch-check">✓</span>}
          </button>
        ))}
      </div>
      <div className="intensity-row">
        <span>Muted</span>
        <input type="range" min={0.3} max={1} step={0.01} value={intensity}
          onChange={(e) => setIntensity(Number(e.target.value))} />
        <span>Vibrant</span>
      </div>
      <p className="muted" style={{ fontSize: 12 }}>
        Black and White set the overall look; the colors change the accent. Drag intensity
        left to soften colors toward a muted, pastel look.
      </p>

      <h2>Legal</h2>
      <div className="settings-card">
        <a className="settings-row" href="/terms" target="_blank" rel="noreferrer"><span>Terms of Use</span><span className="muted">↗</span></a>
        <a className="settings-row" href="/guidelines" target="_blank" rel="noreferrer"><span>Community Guidelines</span><span className="muted">↗</span></a>
        <a className="settings-row" href="/privacy" target="_blank" rel="noreferrer"><span>Privacy Policy</span><span className="muted">↗</span></a>
      </div>

      <button
        className="btn btn-secondary"
        disabled={signingOut}
        onClick={async () => {
          if (signingOut) return;
          setSigningOut(true);
          try {
            await signOutUser();
            navigate("/");
          } catch (e) {
            setError(friendly(e));
            setSigningOut(false);
          }
        }}
      >
        {signingOut ? "Signing out…" : "Sign out"}
      </button>
      <button className="btn btn-ghost" style={{ color: "var(--danger)" }} onClick={() => setConfirmDelete(true)}>
        Delete account
      </button>
      <p className="muted" style={{ fontSize: 12 }}>
        Permanently deletes your profile, photos, matches, and messages. This can't be undone.
      </p>

      {error && <div className="error">{error}</div>}

      <Sheet open={confirmDelete} onClose={() => setConfirmDelete(false)} title="Delete your account?">
        <p className="muted">
          Your profile, photos, matches, and messages will be permanently deleted. This
          can't be undone.
        </p>
        <label>Type DELETE to confirm</label>
        <input value={deleteText} onChange={(e) => setDeleteText(e.target.value)} placeholder="DELETE" />
        <button className="btn btn-danger" disabled={deleteText !== "DELETE" || deleting} onClick={() => void doDelete()}>
          {deleting ? "Deleting…" : "Delete everything"}
        </button>
        <button className="btn btn-ghost" onClick={() => setConfirmDelete(false)}>Cancel</button>
      </Sheet>
    </div>
  );
}
